import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';

import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatStepperModule } from '@angular/material/stepper';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { Subject, debounceTime, take, takeUntil } from 'rxjs';
import { ServiceFonctionnel } from '@models/domains/administration';
import { ServiceFonctionnelPoste } from '@models/domains/posts';
import { AgentsService } from 'src/app/core/services/agents.service';
@Component({
    selector: 'app-destinataires-demande-form',
    imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatStepperModule,
    MatAutocompleteModule
],
    templateUrl: './destinataires-demande-form.component.html',
    styleUrls: ['./destinataires-demande-form.component.scss']
})
export class DestinatairesDemandeFormComponent implements OnInit, OnDestroy {
  // Make Validators available in template
  public ValidatorsRef = Validators;
  @Input() destinataireform!: FormGroup;
  //  @Input() posteService: ServiceFonctionnel;
  @Output() formSubmit = new EventEmitter<FormGroup>();

  private _service: ServiceFonctionnel | ServiceFonctionnelPoste | undefined;
  private _direction: any;
  private readonly destroy$ = new Subject<void>();
  private readonly structureChange$ = new Subject<'direction' | 'service' | 'init'>();
  private lastFormDisabledState = false;

  @Input()
  set service(value: ServiceFonctionnel | ServiceFonctionnelPoste | undefined) {
    const previousId = this._service?.id ?? null;
    this._service = value;
    const nextId = this._service?.id ?? null;
    if (previousId !== nextId) {
      this.triggerStructureReload('service');
    }
  }
  get service(): ServiceFonctionnel | ServiceFonctionnelPoste | undefined {
    return this._service;
  }

  @Input()
  set direction(value: any) {
    const previousId = this._direction?.id ?? null;
    this._direction = value;
    const nextId = this._direction?.id ?? null;
    if (previousId !== nextId) {
      this.triggerStructureReload('direction');
    }
  }
  get direction(): any {
    return this._direction;
  }

  filteredOptionsDirecteur: any = [];
  filteredOptionsGpeec: any = [];
  filteredOptionsDrh: any = [];
  filteredOptionsDg: any = [];
  filteredOptionsResponsable: any = [];
  filteredOptionsDgs: any = [];
  filteredOptionsArchivageRh: any = [];

  val_ft_destinataireValidation: any = null;
  val_ft_destinataireGpeec: any = null;
  val_ft_destinataireDrh: any = null;
  val_ft_destinataireDg: any = null;
  val_ft_destinataireResponsable: any = null;
  val_ft_destinataireDgs: any = null;
  val_ft_destinataireArchivageRh: any = null;

  showDestinataireDirecteurField: boolean = true;
  showDestinataireGpeecField: boolean = true;
  showDestinataireDrhField: boolean = true;
  showDestinataireDgField: boolean = true;
  showDestinataireResponsableField: boolean = true;
  showDestinataireDgsField: boolean = true;
  showDestinataireArchivageRhField: boolean = true;

  libelleGpeecListe = 'A POSITIONNER';

  constructor(
    private fb: FormBuilder,
    private agentsService: AgentsService,
    private cdRef: ChangeDetectorRef
  ) {}

  // Hooks up subscriptions to keep validator lists in sync with form changes.
  ngOnInit() {
    this.structureChange$
      .pipe(debounceTime(0), takeUntil(this.destroy$))
      .subscribe((reason) => this.reloadStructureDependentDestinataires(reason));

    this.lastFormDisabledState = this.isReadOnlyMode();
    if (!this.lastFormDisabledState) {
      this.initializeDestinatairesData();
    } else {
      this.populateReadOnlyOptions();
    }

    this.destinataireform.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      const currentlyDisabled = this.isReadOnlyMode();
      if (this.lastFormDisabledState && !currentlyDisabled) {
        this.initializeDestinatairesData();
      } else if (!this.lastFormDisabledState && currentlyDisabled) {
        this.populateReadOnlyOptions();
      }
      this.lastFormDisabledState = currentlyDisabled;
    });
  }

  // Releases all subscriptions when the component is destroyed.
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Schedules a refresh for structure-dependent validator lookups.
  private triggerStructureReload(reason: 'direction' | 'service' | 'init' = 'init'): void {
    this.structureChange$.next(reason);
  }

  // Recomputes validator lists after direction/service changes in editable mode.
  private reloadStructureDependentDestinataires(reason: 'direction' | 'service' | 'init' = 'init'): void {
    if (this.isReadOnlyMode()) {
      return;
    }

    const directionId = this.direction?.id ?? null;
    const serviceId = this.service?.id ?? null;

    if (directionId != null) {
      if (reason !== 'service') {
        this.loadDestinataireDirecteur();
        this.loadDestinataireDg();
      }
      this.loadDestinataireArchivageRh();
    } else {
      this.hideDestinataireDirecteurField();
      this.hideDestinataireDgField();
      this.hideDestinataireArchivageRhField();
    }

    if (serviceId != null && directionId != null) {
      this.loadDestinataireResponsable();
    } else {
      this.hideDestinataireResponsableField();
    }

    if (directionId != null) {
      this.loadDestinataireGpeec();
    } else {
      this.hideDestinataireGpeecField();
    }
  }

  // Emits the child form so the parent stepper can validate the step.
  onSubmit() {
    this.formSubmit.emit();
  }

  // Clears the validator selections and resets validation state.
  onReset() {
    this.destinataireform.reset();
  }
  // Loads Directeur validators for the chosen direction while the form is editable.
  loadDestinataireDirecteur() {
    if (this.isReadOnlyMode()) {
      return;
    }

    const directionId = this.direction?.id ?? null;
    if (!directionId) {
      this.hideDestinataireDirecteurField();
      return;
    }

    const control = this.destinataireform.get('destinataireValidationDirecteur');
    control?.reset(null, { emitEvent: false });
    control?.setValidators(Validators.required);
    control?.enable({ emitEvent: false });

    this.agentsService
      .getDestinataireDirecteur('Recrutement', directionId)
      .pipe(take(1))
      .subscribe({
        next: (data) => {
          if ((this.direction?.id ?? null) !== directionId) {
            return;
          }
          const valideurs = data.valideurs || [];

          if (!valideurs.length) {
            this.hideDestinataireDirecteurField();
            return;
          }
          this.showDestinataireDirecteurField = true;
          this.filteredOptionsDirecteur = valideurs;
          const defaultDirecteur = this.filteredOptionsDirecteur[0] ?? null;
          if (defaultDirecteur) {
            this.val_ft_destinataireValidation = defaultDirecteur;
            control?.setValue(defaultDirecteur, { emitEvent: false });
          }
          control?.updateValueAndValidity({ emitEvent: false });
        },
        error: (error) => console.error('Erreur lors de la récupération Directeur :', error)
      });
  }

  // Loads DRH validators once while the form stays editable.
  loadDestinataireDrh() {
    this.agentsService
      .getDestinataireDRH('Recrutement')
      .pipe(take(1))
      .subscribe({
        next: (data) => {
          let valideurs = data.valideurs;
          let typeValideurs = data.type;

          if (!valideurs.length) {
            this.hideDestinataireDrhField();
            return;
          }
          this.filteredOptionsDrh = valideurs;
          const defaultDrh = this.filteredOptionsDrh[0];
          if (defaultDrh) {
            this.val_ft_destinataireDrh = defaultDrh;
            this.destinataireform.get('destinataireDrh')?.setValue(defaultDrh);
          }
        },
        error: (error) => console.error('Erreur lors de la récupération DRH :', error)
      });
  }

  // Loads DG validators whenever the editable direction changes.
  loadDestinataireDg() {
    if (this.isReadOnlyMode()) {
      return;
    }

    const directionId = this.direction?.id ?? null;
    if (!directionId) {
      this.hideDestinataireDgField();
      return;
    }

    const control = this.destinataireform.get('destinataireDg');
    control?.reset(null, { emitEvent: false });
    control?.setValidators(Validators.required);
    control?.enable({ emitEvent: false });

    this.agentsService
      .getDestinataireDG('Recrutement', directionId)
      .pipe(take(1))
      .subscribe({
        next: (data) => {
          if ((this.direction?.id ?? null) !== directionId) {
            return;
          }
          const valideurs = data.valideurs || [];

          if (!valideurs.length) {
            this.hideDestinataireDgField();
            return;
          }
          this.showDestinataireDgField = true;
          this.filteredOptionsDg = valideurs;
          const defaultDg = this.filteredOptionsDg[0] ?? null;
          if (defaultDg) {
            this.val_ft_destinataireDg = defaultDg;
            control?.setValue(defaultDg, { emitEvent: false });
          }
          control?.updateValueAndValidity({ emitEvent: false });
        },
        error: (error) => console.error('Erreur lors de la récupération DG :', error)
      });
  }

  // Loads GPEEC validators for the selected direction (service optional).
  loadDestinataireGpeec() {
    if (this.isReadOnlyMode()) {
      return;
    }

    const directionId = this.direction?.id ?? null;
    const serviceId = this.service?.id ?? null;

    if (!directionId) {
      this.hideDestinataireGpeecField();
      return;
    }

    const control = this.destinataireform.get('destinataireChargeGpeec');
    control?.reset(null, { emitEvent: false });
    control?.setValidators(Validators.required);
    control?.enable({ emitEvent: false });

    this.agentsService
      .getChargedGPEEC('Recrutement', directionId, serviceId)
      .pipe(take(1))
      .subscribe({
        next: (data) => {
          if ((this.direction?.id ?? null) !== directionId || (this.service?.id ?? null) !== serviceId) {
            return;
          }
          const valideurs = data.valideurs || [];
          const typeValideurs = data.type;

          if (typeValideurs == 'GESTIONNAIRE_STATUTAIRE') {
            this.libelleGpeecListe = 'Gestionnaire Statutaire';
          } else if (typeValideurs == 'CHARGES_GESTION_RH') {
            this.libelleGpeecListe = 'Chargé de gestion RH';
          } else if (typeValideurs == 'CHARGES_GESTION_GPEEC') {
            this.libelleGpeecListe = 'Chargé de gestion GPEEC';
          } else {
            this.libelleGpeecListe = 'Non positionné';
          }

          if (!valideurs.length) {
            this.hideDestinataireGpeecField();
            return;
          }
          this.showDestinataireGpeecField = true;
          this.filteredOptionsGpeec = valideurs;
          const defaultGpeec = this.filteredOptionsGpeec[0] ?? null;
          if (defaultGpeec) {
            this.val_ft_destinataireGpeec = defaultGpeec;
            control?.setValue(defaultGpeec, { emitEvent: false });
          }
          control?.updateValueAndValidity({ emitEvent: false });
        },
        error: (error) => console.error('Erreur lors de la récupération GPEEC:', error)
      });
  }

  // Loads DGS validators used to populate the select during editing.
  loadDestinataireDgs() {
    this.agentsService
      .getDestinataireDGS('Recrutement')
      .pipe(take(1))
      .subscribe({
        next: (data) => {
          let valideurs = data.valideurs;
          let typeValideurs = data.type;

          if (!valideurs.length) {
            this.hideDestinataireDgsField();
            return;
          }
          this.filteredOptionsDgs = valideurs;
          const defaultDgs = this.filteredOptionsDgs[0];
          if (defaultDgs) {
            this.val_ft_destinataireDgs = defaultDgs;
            this.destinataireform.get('destinataireDgs')?.setValue(defaultDgs);
          }
        },
        error: (error) => console.error('Erreur lors de la récupération DGS :', error)
      });
  }

  // Loads the Archivage RH validator tied to the selected direction while editing.
  loadDestinataireArchivageRh() {
    // Archivage RH is not reloaded when the form is disabled during consultation.
    if (this.isReadOnlyMode()) {
      return;
    }

    const directionId = this.direction?.id ?? null;
    const serviceId = this.service?.id ?? null;
    if (directionId == null) {
      this.hideDestinataireArchivageRhField();
      return;
    }

    const control = this.destinataireform.get('destinataireArchivageRh');
    control?.reset(null, { emitEvent: false });
    control?.setValidators(Validators.required);
    control?.enable({ emitEvent: false });

    this.agentsService
      .getDestinataireArchivageRh('Recrutement', directionId, serviceId)
      .pipe(take(1))
      .subscribe({
        next: (data) => {
          if ((this.direction?.id ?? null) !== directionId || (this.service?.id ?? null) !== serviceId) {
            return;
          }
          let valideurs = data.valideurs;
          let typeValideurs = data.type;

          if (!valideurs.length) {
            this.hideDestinataireArchivageRhField();
            return;
          }
          this.showDestinataireArchivageRhField = true;
          this.filteredOptionsArchivageRh = valideurs;
          const defaultArchivageRh = this.filteredOptionsArchivageRh[0];
          if (defaultArchivageRh) {
            this.val_ft_destinataireArchivageRh = defaultArchivageRh;
            control?.setValue(defaultArchivageRh, { emitEvent: false });
          }
          control?.updateValueAndValidity({ emitEvent: false });
        },
        error: (error) => console.error('Erreur lors de la récupération Archivage RH :', error)
      });
  }

  // Loads Responsable validators for the selected service and direction when editing.
  loadDestinataireResponsable() {
    // Responsable validators rely on user input and are not fetched in read-only mode.
    if (this.isReadOnlyMode()) {
      return;
    }

    const serviceId = this.service?.id ?? null;
    const directionId = this.direction?.id ?? null;
    if (serviceId == null || directionId == null) {
      this.hideDestinataireResponsableField();
      return;
    }

    const control = this.destinataireform.get('destinataireResponsable');
    control?.reset(null, { emitEvent: false });
    control?.setValidators(Validators.required);
    control?.enable({ emitEvent: false });

    this.agentsService
      .getDestinataireResponsable('Recrutement', serviceId, directionId)
      .pipe(take(1))
      .subscribe({
        next: (data) => {
          if ((this.direction?.id ?? null) !== directionId || (this.service?.id ?? null) !== serviceId) {
            return;
          }
          const valideurs = data.valideurs || [];

          if (!valideurs.length) {
            this.hideDestinataireResponsableField();
            return;
          }
          this.enableDestinataireResponsableField();
          this.filteredOptionsResponsable = valideurs;
          const defaultResponsable = this.filteredOptionsResponsable[0] ?? null;
          if (defaultResponsable) {
            this.val_ft_destinataireResponsable = defaultResponsable;
            control?.setValue(defaultResponsable, { emitEvent: false });
          }
          control?.updateValueAndValidity({ emitEvent: false });
        },
        error: (error) => console.error('Erreur lors de la récupération Responsable :', error)
      });
  }

  // Preloads validator lists needed while the user can modify the form.
  private initializeDestinatairesData(): void {
    this.loadDestinataireDrh();
    this.loadDestinataireDgs();
    this.triggerStructureReload('init');
  }

  // Rehydrates dropdown options from persisted values when in read-only mode.
  private populateReadOnlyOptions(): void {
    const raw = this.destinataireform?.getRawValue?.() ?? {};

    const responsable = raw.destinataireResponsable ?? null;
    this.filteredOptionsResponsable = this.toArray(responsable);
    this.val_ft_destinataireResponsable = responsable;
    this.showDestinataireResponsableField = !!responsable;

    const directeur = raw.destinataireValidationDirecteur ?? null;
    this.filteredOptionsDirecteur = this.toArray(directeur);
    this.val_ft_destinataireValidation = directeur;
    this.showDestinataireDirecteurField = !!directeur;

    const gpeec = raw.destinataireChargeGpeec ?? null;
    this.filteredOptionsGpeec = this.toArray(gpeec);
    this.val_ft_destinataireGpeec = gpeec;
    this.showDestinataireGpeecField = !!gpeec;

    const drh = raw.destinataireDrh ?? null;
    this.filteredOptionsDrh = this.toArray(drh);
    this.val_ft_destinataireDrh = drh;
    this.showDestinataireDrhField = !!drh;

    const dg = raw.destinataireDg ?? null;
    this.filteredOptionsDg = this.toArray(dg);
    this.val_ft_destinataireDg = dg;
    this.showDestinataireDgField = !!dg;

    const dgs = raw.destinataireDgs ?? null;
    this.filteredOptionsDgs = this.toArray(dgs);
    this.val_ft_destinataireDgs = dgs;
    this.showDestinataireDgsField = !!dgs;

    const archivage = raw.destinataireArchivageRh ?? null;
    this.filteredOptionsArchivageRh = this.toArray(archivage);
    this.val_ft_destinataireArchivageRh = archivage;
    this.showDestinataireArchivageRhField = !!archivage;
  }

  // Wraps a single validator value into a list that mat-select can consume.
  private toArray<T>(value: T | null | undefined): T[] {
    return value ? [value] : [];
  }

  // Indicates whether the validator form is currently locked for reading.
  private isReadOnlyMode(): boolean {
    return this.destinataireform?.disabled ?? false;
  }

  // Disables and clears the DG field when no candidate exists.
  hideDestinataireDgField() {
    const destinataireDgControl = this.destinataireform.get('destinataireDg');
    if (destinataireDgControl) {
      destinataireDgControl.reset(null, { emitEvent: false });
      destinataireDgControl.setValidators(null);
      destinataireDgControl.disable({ emitEvent: false });
      destinataireDgControl.updateValueAndValidity({ emitEvent: false });
    }

    this.showDestinataireDgField = false;
  }

  // Disables and clears the GPEEC field when no validator is found.
  hideDestinataireGpeecField() {
    const destinataireGpeecControl = this.destinataireform.get('destinataireChargeGpeec');
    if (destinataireGpeecControl) {
      destinataireGpeecControl.reset(null, { emitEvent: false });
      destinataireGpeecControl.setValidators(null);
      destinataireGpeecControl.disable({ emitEvent: false });
      destinataireGpeecControl.updateValueAndValidity({ emitEvent: false });
    }

    this.showDestinataireGpeecField = false;
  }

  // Disables and clears the DRH field when the service returns empty.
  hideDestinataireDrhField() {
    const destinataireDrhControl = this.destinataireform.get('destinataireDrh');
    if (destinataireDrhControl) {
      destinataireDrhControl.reset(null, { emitEvent: false });
      destinataireDrhControl.setValidators(null);
      destinataireDrhControl.disable({ emitEvent: false });
      destinataireDrhControl.updateValueAndValidity({ emitEvent: false });
    }

    this.showDestinataireDrhField = false;
  }

  // Disables and clears the Directeur field when unavailable.
  hideDestinataireDirecteurField() {
    const destinataireValidationControl = this.destinataireform.get('destinataireValidationDirecteur');
    if (destinataireValidationControl) {
      destinataireValidationControl.reset(null, { emitEvent: false });
      destinataireValidationControl.setValidators(null);
      destinataireValidationControl.disable({ emitEvent: false });
      destinataireValidationControl.updateValueAndValidity({ emitEvent: false });
    }
    this.showDestinataireDirecteurField = false;
  }

  // Disables and clears the Responsable field when no data comes back.
  hideDestinataireResponsableField() {
    const destinataireResponsableControl = this.destinataireform.get('destinataireResponsable');
    if (destinataireResponsableControl) {
      destinataireResponsableControl.reset(null, { emitEvent: false });
      destinataireResponsableControl.setValidators(null);
      destinataireResponsableControl.disable({ emitEvent: false });
      destinataireResponsableControl.updateValueAndValidity({ emitEvent: false });
    }

    this.showDestinataireResponsableField = false;
  }

  // Enables the Responsable field and reapplies its validation rules.
  enableDestinataireResponsableField() {
    const destinataireResponsableControl = this.destinataireform.get('destinataireResponsable');
    if (destinataireResponsableControl) {
      destinataireResponsableControl.setValidators(Validators.required);
      destinataireResponsableControl.enable();
    }

    this.showDestinataireResponsableField = true;
  }

  // Disables and clears the DGS field when no data is returned.
  hideDestinataireDgsField() {
    const destinataireDgsControl = this.destinataireform.get('destinataireDgs');
    if (destinataireDgsControl) {
      destinataireDgsControl.reset(null, { emitEvent: false });
      destinataireDgsControl.setValidators(null);
      destinataireDgsControl.disable({ emitEvent: false });
      destinataireDgsControl.updateValueAndValidity({ emitEvent: false });
    }

    this.showDestinataireDgsField = false;
  }

  // Disables and clears the Archivage RH field when unassigned.
  hideDestinataireArchivageRhField() {
    const destinataireArchivageRhControl = this.destinataireform.get('destinataireArchivageRh');
    if (destinataireArchivageRhControl) {
      destinataireArchivageRhControl.reset(null, { emitEvent: false });
      destinataireArchivageRhControl.setValidators(null);
      destinataireArchivageRhControl.disable({ emitEvent: false });
      destinataireArchivageRhControl.updateValueAndValidity({ emitEvent: false });
    }

    this.showDestinataireArchivageRhField = false;
  }
}
